import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfigurationServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'bo._base_domain_': [
      {
        srcPath: '/_api/bo-payment-acceptance-configurations/v1',
        destPath: '/v1/bo-payment-acceptance-configurations',
      },
      {
        srcPath: '/v1/bo-payment-acceptance-configurations',
        destPath: '/v1/bo-payment-acceptance-configurations',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/bo-payment-acceptance-configurations/v1',
        destPath: '/v1/bo-payment-acceptance-configurations',
      },
      {
        srcPath: '/v1/bo-payment-acceptance-configurations',
        destPath: '/v1/bo-payment-acceptance-configurations',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/bo-payment-acceptance-configurations/v1',
        destPath: '/v1/bo-payment-acceptance-configurations',
      },
      {
        srcPath: '/v1/bo-payment-acceptance-configurations',
        destPath: '/v1/bo-payment-acceptance-configurations',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/payment-acceptance-configurations',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_payments_payment-acceptance-configurations';

/** Creates a PaymentAcceptanceConfiguration. */
export function createPaymentAcceptanceConfiguration(
  payload: object
): RequestOptionsFactory<any> {
  function __createPaymentAcceptanceConfiguration({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'paymentAcceptanceConfiguration.createdDate' },
          { path: 'paymentAcceptanceConfiguration.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn:
        'wix.payments.payment_acceptance_configurations.v1.payment_acceptance_configuration',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.CreatePaymentAcceptanceConfiguration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfigurationServiceUrl(
        {
          protoPath: '/v1/payment-acceptance-configurations',
          data: serializedData,
          host,
        }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'paymentAcceptanceConfiguration.createdDate' },
              { path: 'paymentAcceptanceConfiguration.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createPaymentAcceptanceConfiguration;
}

/** Retrieves a PaymentAcceptanceConfiguration. */
export function getPaymentAcceptanceConfiguration(
  payload: object
): RequestOptionsFactory<any> {
  function __getPaymentAcceptanceConfiguration({ host }: any) {
    const metadata = {
      entityFqdn:
        'wix.payments.payment_acceptance_configurations.v1.payment_acceptance_configuration',
      method: 'GET' as any,
      methodFqn:
        'wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.GetPaymentAcceptanceConfiguration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfigurationServiceUrl(
        {
          protoPath:
            '/v1/payment-acceptance-configurations/{paymentAcceptanceConfigurationId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'paymentAcceptanceConfiguration.createdDate' },
              { path: 'paymentAcceptanceConfiguration.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getPaymentAcceptanceConfiguration;
}

/**
 * Updates a PaymentAcceptanceConfiguration.
 *
 *
 * Each time the PaymentAcceptanceConfiguration is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the PaymentAcceptanceConfiguration.
 * This ensures you're working with the latest PaymentAcceptanceConfiguration
 * and prevents unintended overwrites.
 */
export function updatePaymentAcceptanceConfiguration(
  payload: object
): RequestOptionsFactory<any> {
  function __updatePaymentAcceptanceConfiguration({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'paymentAcceptanceConfiguration.createdDate' },
          { path: 'paymentAcceptanceConfiguration.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn:
        'wix.payments.payment_acceptance_configurations.v1.payment_acceptance_configuration',
      method: 'PATCH' as any,
      methodFqn:
        'wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.UpdatePaymentAcceptanceConfiguration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfigurationServiceUrl(
        {
          protoPath:
            '/v1/payment-acceptance-configurations/{paymentAcceptanceConfiguration.id}',
          data: serializedData,
          host,
        }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'paymentAcceptanceConfiguration.createdDate' },
              { path: 'paymentAcceptanceConfiguration.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updatePaymentAcceptanceConfiguration;
}

/**
 * Add PaymentAcceptanceConfiguration Rules
 * as result new rules will be validated and added to existing ones
 */
export function addPaymentAcceptanceConfigurationRules(
  payload: object
): RequestOptionsFactory<any> {
  function __addPaymentAcceptanceConfigurationRules({ host }: any) {
    const metadata = {
      entityFqdn:
        'wix.payments.payment_acceptance_configurations.v1.payment_acceptance_configuration',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.AddPaymentAcceptanceConfigurationRules',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfigurationServiceUrl(
        {
          protoPath:
            '/v1/payment-acceptance-configurations/{paymentAcceptanceConfigurationId}/add-rules',
          data: payload,
          host,
        }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'paymentAcceptanceConfiguration.createdDate' },
              { path: 'paymentAcceptanceConfiguration.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addPaymentAcceptanceConfigurationRules;
}

/**
 * Remove PaymentAcceptanceConfiguration
 * as result existing rules will be removed from PaymentAcceptanceConfiguration
 * some extra rules may be deleted
 */
export function removePaymentAcceptanceConfigurationRules(
  payload: object
): RequestOptionsFactory<any> {
  function __removePaymentAcceptanceConfigurationRules({ host }: any) {
    const metadata = {
      entityFqdn:
        'wix.payments.payment_acceptance_configurations.v1.payment_acceptance_configuration',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.RemovePaymentAcceptanceConfigurationRules',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfigurationServiceUrl(
        {
          protoPath:
            '/v1/payment-acceptance-configurations/{paymentAcceptanceConfigurationId}/remove-rules',
          data: payload,
          host,
        }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'paymentAcceptanceConfiguration.createdDate' },
              { path: 'paymentAcceptanceConfiguration.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __removePaymentAcceptanceConfigurationRules;
}

/**
 * Modify PaymentAcceptanceConfiguration Rules
 * as result new rules will be validated and added to existing ones
 * rules to removed will be removed, may remove extra rules depending on app_id specific logic
 * some extra rules may be added
 * rules_to_update allows partial updates to existing rules using field masks
 */
export function modifyPaymentAcceptanceConfigurationRules(
  payload: object
): RequestOptionsFactory<any> {
  function __modifyPaymentAcceptanceConfigurationRules({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'maskedRulesToUpdate.fieldMask' }],
      },
    ]);
    const metadata = {
      entityFqdn:
        'wix.payments.payment_acceptance_configurations.v1.payment_acceptance_configuration',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.ModifyPaymentAcceptanceConfigurationRules',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfigurationServiceUrl(
        {
          protoPath:
            '/v1/payment-acceptance-configurations/{paymentAcceptanceConfigurationId}/modify-rules',
          data: serializedData,
          host,
        }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'paymentAcceptanceConfiguration.createdDate' },
              { path: 'paymentAcceptanceConfiguration.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __modifyPaymentAcceptanceConfigurationRules;
}

/** Delete a PaymentAcceptanceConfiguration */
export function deletePaymentAcceptanceConfiguration(
  payload: object
): RequestOptionsFactory<any> {
  function __deletePaymentAcceptanceConfiguration({ host }: any) {
    const metadata = {
      entityFqdn:
        'wix.payments.payment_acceptance_configurations.v1.payment_acceptance_configuration',
      method: 'DELETE' as any,
      methodFqn:
        'wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.DeletePaymentAcceptanceConfiguration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfigurationServiceUrl(
        {
          protoPath:
            '/v1/payment-acceptance-configurations/{paymentAcceptanceConfigurationId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deletePaymentAcceptanceConfiguration;
}

/**
 * Retrieves a list of PaymentAcceptanceConfigurations, given the provided [paging, filtering, and sorting][1].
 *
 * Up to 1,000 PaymentAcceptanceConfigurations can be returned per request.
 *
 * To learn how to query PaymentAcceptanceConfigurations, see [API Query Language][2].
 *
 * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
 * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
 */
export function queryPaymentAcceptanceConfigurations(
  payload: object
): RequestOptionsFactory<any> {
  function __queryPaymentAcceptanceConfigurations({ host }: any) {
    const metadata = {
      entityFqdn:
        'wix.payments.payment_acceptance_configurations.v1.payment_acceptance_configuration',
      method: 'GET' as any,
      methodFqn:
        'wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.QueryPaymentAcceptanceConfigurations',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfigurationServiceUrl(
        {
          protoPath: '/v1/payment-acceptance-configurations/query',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'paymentAcceptanceConfigurations.createdDate' },
              { path: 'paymentAcceptanceConfigurations.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfigurationServiceUrl(
            {
              protoPath: '/v1/payment-acceptance-configurations/query',
              data: payload,
              host,
            }
          ),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryPaymentAcceptanceConfigurations;
}

/**
 * Synchronously update tags on multiple PaymentAcceptanceConfigurations, by list of PaymentAcceptanceConfigurations ids
 * A tag that appears both in the list of assign and unassign tags, will be assigned
 */
export function bulkUpdatePaymentAcceptanceConfigurationTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdatePaymentAcceptanceConfigurationTags({ host }: any) {
    const metadata = {
      entityFqdn:
        'wix.payments.payment_acceptance_configurations.v1.payment_acceptance_configuration',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.BulkUpdatePaymentAcceptanceConfigurationTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfigurationServiceUrl(
        {
          protoPath: '/v1/bulk/payment-acceptance-configurations/update-tags',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdatePaymentAcceptanceConfigurationTags;
}

/**
 * Asynchronously update tags on multiple PaymentAcceptanceConfigurations, by provided filter
 * An empty filter will update all PaymentAcceptanceConfigurations
 * A tag that appears both in the list of assign and unassign tags, will be assigned
 */
export function bulkUpdatePaymentAcceptanceConfigurationTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdatePaymentAcceptanceConfigurationTagsByFilter({
    host,
  }: any) {
    const metadata = {
      entityFqdn:
        'wix.payments.payment_acceptance_configurations.v1.payment_acceptance_configuration',
      method: 'POST' as any,
      methodFqn:
        'wix.payments.payment_acceptance_configurations.v1.PaymentAcceptanceConfigurationService.BulkUpdatePaymentAcceptanceConfigurationTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPaymentsPaymentAcceptanceConfigurationsV1PaymentAcceptanceConfigurationServiceUrl(
        {
          protoPath:
            '/v1/bulk/payment-acceptance-configurations/update-tags-by-filter',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdatePaymentAcceptanceConfigurationTagsByFilter;
}
